import { AfterViewChecked, Component, ElementRef, EventEmitter, Inject, Input,
         OnChanges, OnDestroy, Output, SimpleChange
       } from '@angular/core';
import { MatIconRegistry } from '@angular/material';
import { DomSanitizer } from '@angular/platform-browser';

import { TemplateConfig, TemplateGroupConfig, TemplatesSettings
       } from 'app/collage-designer/collage-designer-config';
import { DesignStepService
       } from 'app/collage-designer/design-step/design-step.service';
import { PlaceholderType
       } from 'app/collage-designer/stage/placeholder/placeholder';
import { PlaceholderService
       } from 'app/collage-designer/stage/placeholder/placeholder.service';
import { GlobalConstants, ToolboxConstants } from 'core/constants';
import { Globals } from 'core/globals';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitterSubscriber } from 'core/utils/async-event-emitter';
import { GalleryImage, GalleryService } from '../../gallery/gallery.service';

interface TemplateSelection
{
  templateId: string;
  templateGroupId: string;
}

@Component({
  selector: 'template-chooser',
  templateUrl: './template-chooser.component.html',
  styleUrls: ['./template-chooser.component.scss', './modal.scss'],
})
export class TemplateChooserComponent
    implements AfterViewChecked, OnChanges, OnDestroy {
  private static readonly TEMPLATE_GROUPS_PER_PAGE: number = 3;
  private newImageAvaiableSubscription:
      AsyncEventEmitterSubscriber<GalleryImage>;
  private _templateGroupSelectionIdx: number | undefined = undefined;
  private visitedTemplateGroups: {[id: string]: boolean} = {};
  private lastVisitedPage: number = 1;

  @Input() public templateGroups: Array<TemplateGroupConfig>;
  @Input() public templatesSettings: TemplatesSettings;

  @Input() public selectedTemplateId: string;
  @Output() public selectedTemplateIdChange: EventEmitter<string> =
      new EventEmitter();

  @Input() public selectedTemplateGroupId: string;

  @Output() public selectedTemplateChange: EventEmitter<TemplateSelection> =
      new EventEmitter();
  @Output() public selectedTemplateNotChange: EventEmitter<TemplateSelection> =
      new EventEmitter();

  public groups: Array<TemplateGroupConfig> = [];
  public templateGroupSelection: string | undefined = undefined;

  public trackById: Object = Utils.trackById;

  public constructor(
      private el: ElementRef,
      private mir: MatIconRegistry,
      private sanitizer: DomSanitizer,
      @Inject(GalleryService) private galleryService: GalleryService,
      @Inject(PlaceholderService)
          private placeholderService: PlaceholderService,
      @Inject(ProductService) private productService: ProductService,
      @Inject(EventTrackingService) private eventTrackingService:
          EventTrackingService,
      @Inject(DesignStepService) private designStepService: DesignStepService)
  {
    this.newImageAvaiableSubscription = this.galleryService.newImageAvailable
      .subscribe(this.updateSelectedTemplateByImage.bind(this));

    this.mir.addSvgIcon(
        'close',
        this.sanitizer.bypassSecurityTrustResourceUrl(
            'assets/img/icons/close.svg'));
  }

  public ngAfterViewChecked(): void {
    this.loadPreviews();
  }

  public async ngOnChanges(
      changes: {[propKey: string]: SimpleChange}): Promise<void>
  {
    if (changes['templateGroups'] &&
        this.designStepService.getPart().templateAutoChange &&
        this.templateGroups.length > 0 &&
        this.galleryService.getImages(true).length > 0) {
      await this.updateSelectedTemplateByImage(
          this.galleryService.getImages(true)[0]);
    }
  }

  private async updateSelectedTemplateByImage(
      image: GalleryImage): Promise<void>
  {
    const alreadyScored = !this.designStepService.getPart().templateAutoChange;
    const isGodMode = Globals.mode === GlobalConstants.MODE_GOD;
    const hasFixedTemplate =
      this.designStepService.getPart().hasPreselectedTemplate;
    const hasChangedTextPlaceholder =
        this.placeholderService.getUnchangedPlaceholders()
          .filter(p => p.type === PlaceholderType.Text).length <
        this.placeholderService.getTextPlaceholders().length;

    if (alreadyScored || isGodMode || hasFixedTemplate ||
        hasChangedTextPlaceholder) {
      return;
    }

    const imageOrientation =
        image.width >= image.height ? 'landscape' : 'portrait';

    const templates = Utils.flattenArray(
      this.templateGroups
        .map((group: TemplateGroupConfig) =>
          Object.keys(group.templates)
            .map((id) => group.templates[id])
            .filter(t => t.imagePlaceholders[imageOrientation] === 1)
            .map(template => ({
              templateGroupId: group.id,
              templateId: template.id,
            }))));

    if (templates.length === 0) {
      return;
    }

    await this.updateSelectedTemplate(
        templates[0].templateGroupId, templates[0].templateId, true);
  }

  public ngOnDestroy(): void
  {
    this.galleryService.newImageAvailable.unsubscribe(
        this.newImageAvaiableSubscription);
  }

  public getTemplates(templateGroupId?: string): Array<TemplateConfig>
  {
    if (!templateGroupId) {
      templateGroupId = this.selectedTemplateGroupId;
    }

    const _t = Utils.find(
        this.templateGroups, g => g.id === templateGroupId).templates;
    return Object.keys(_t).map(key => _t[key]);
  }

  public async updateSelectedTemplate(
      templateGroupId: string, templateId: string, autoSelect: boolean = false):
      Promise<void>
  {
    this.placeholderService.resetPendingPlaceholders();

    this.designStepService.getPart().templateAutoChange = false;

    if (this.selectedTemplateGroupId !== templateGroupId ||
        this.selectedTemplateId !== templateId ||
        ToolboxConstants.REFRESH_TEMPLATE_ON_SAME_LAYOUT_SELECTION)
    {
      this.selectedTemplateGroupId = templateGroupId;
      this.selectedTemplateId = templateId;

      this.selectedTemplateChange.emit({
        templateId: templateId,
        templateGroupId: templateGroupId
      });

      this.eventTrackingService.track({
        category: this.productService.getCurrentProduct().id,
        action: 'template_change' + (autoSelect ? '_auto' : ''),
        label: templateId,
      });
    }
    else {
      this.selectedTemplateNotChange.emit();
    }

    this.unsetTemplateGroupSelection();
  }

  public unsetTemplateGroupSelection(): void
  {
    this.templateGroupSelection = undefined;
    const toolboxTabs = document.querySelector('#toolbox-tabs');
    if (toolboxTabs) {
      toolboxTabs.classList.remove('visible-overflow');
    }
  }

  public async templateGroupClicked(templateGroupId: string): Promise<void>
  {
    this.eventTrackingService.track({
      category: this.productService.getCurrentProduct().id,
      action: 'template_group_selected',
      label: templateGroupId,
    });

    const templates = this.getTemplates(templateGroupId);
    if (templates.length === 1) {
      await this.updateSelectedTemplate(templateGroupId, templates[0].id);
    }
    else {
      this.templateGroupSelection = templateGroupId;
      const toolboxTabs = document.querySelector('#toolbox-tabs');
      if (toolboxTabs) {
        toolboxTabs.classList.add('visible-overflow');
      }
      this.updateSelectedTemplateGroupIndex();
    }
  }

  public placeholdersCount(template: TemplateConfig): number
  {
    const emptyCount = { portrait: 0, landscape: 0 };
    const count = template.imagePlaceholders || emptyCount;
    return count.portrait + count.landscape;
  }

  public set templateGroupSelectionIdx(idx: number)
  {
    this._templateGroupSelectionIdx = idx;
    this.visitedTemplateGroups[this.templateGroups[idx].id] = true;
  }

  public get templateGroupSelectionIdx(): number | undefined
  {
    return this._templateGroupSelectionIdx;
  }

  public isVisitedTemplateGroup(id: string): boolean
  {
    return !!this.visitedTemplateGroups[id];
  }

  public updateSelectedTemplateGroupIndex(idx?: number): number
  {
    if (idx === undefined) {
      this.templateGroupSelectionIdx =
          this.templateGroups.findIndex(
              (el) => el.id === this.templateGroupSelection);
    }
    else {
      if (idx >= 0 && idx < this.templateGroups.length) {
        this.templateGroupSelectionIdx = idx;
      }
    }

    this.designStepService.getPart().templateAutoChange = false;
    return this.templateGroups.findIndex(
        (el) => el.id === this.templateGroupSelection);
  }

  public pageChanged(page: number): void
  {
    this.lastVisitedPage = page;
  }

  public currentPage(): number
  {
    return this.lastVisitedPage;
  }

  public itemsPerPage(): number
  {
    if (Utils.isSmallDevice() || Utils.isMediumDevice()) {
      return (
          this.templateGroups.length > 1
          ? this.templateGroups.length
          : this.getTemplates(this.templateGroups[0].id).length);
    }
    return TemplateChooserComponent.TEMPLATE_GROUPS_PER_PAGE;
  }

  private loadPreviews(): void
  {
    const previewImages =
        this.el.nativeElement.querySelectorAll('.preview-image');
    if (!previewImages) {
      return;
    }
    previewImages.forEach(e => {
      if (e.src.endsWith('loading.gif') && e.height > 0 && e.width > 0) {
        const url = e.dataset.src;
        const seperator = url.includes('?') ? '&' : '?';
        const limit = 'limit=' +
                      encodeURIComponent(JSON.stringify({
                        'width': Utils.steppedLimit(e.width),
                        'height': Utils.steppedLimit(e.height)
                      }));

        e.src = url + seperator + limit;
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/controls/template-chooser/template-chooser.component.ts